/**
 * Password Reset Service for DeckOps.ai
 * Handles secure password reset functionality with professional email templates
 */

import { auth, db } from '../firebaseConfig.js';
import emailTemplateService from './EmailTemplateService.js';

class PasswordResetService {
    constructor() {
        console.log('🔒 PasswordResetService initialized');
    }

    /**
     * Send password reset email with professional template
     */
    async sendPasswordResetEmail(email) {
        try {
            console.log(`🔒 Sending password reset email to: ${email}`);

            // Check if user exists
            const userQuery = await db.collection('userProfiles')
                .where('email', '==', email)
                .limit(1)
                .get();

            let displayName = email.split('@')[0];
            
            if (!userQuery.empty) {
                const userData = userQuery.docs[0].data();
                displayName = userData.displayName || displayName;
                console.log('✅ User found in profiles:', displayName);
            } else {
                console.log('⚠️ User not found in profiles, proceeding with email-based reset');
            }

            // Send Firebase password reset email using custom template
            await auth.sendPasswordResetEmail(email, {
                handleCodeInApp: false,
                url: 'https://deckops.ai/login'
            });

            console.log('✅ Firebase password reset email sent');

            // Optional: Send our custom professional template as well
            // This provides a better user experience alongside Firebase's reset
            const resetLink = `https://deckops.ai/reset-password?email=${encodeURIComponent(email)}`;
            
            const emailResult = await emailTemplateService.sendPasswordResetEmail(
                email, 
                displayName, 
                resetLink
            );

            if (emailResult.success) {
                console.log('✅ Professional password reset email sent');
            } else {
                console.warn('⚠️ Professional email failed, but Firebase reset still sent');
            }

            return { 
                success: true, 
                message: 'Password reset email sent successfully' 
            };

        } catch (error) {
            console.error('❌ Password reset error:', error);
            
            let message = 'Unable to send password reset email. Please try again.';
            
            if (error.code === 'auth/user-not-found') {
                message = 'No account found with this email address.';
            } else if (error.code === 'auth/invalid-email') {
                message = 'Please enter a valid email address.';
            } else if (error.code === 'auth/too-many-requests') {
                message = 'Too many password reset requests. Please wait before trying again.';
            }

            return { 
                success: false, 
                message: message,
                code: error.code 
            };
        }
    }

    /**
     * Verify password reset code and allow password change
     */
    async verifyResetCode(code, newPassword) {
        try {
            console.log('🔒 Verifying password reset code...');

            await auth.confirmPasswordReset(code, newPassword);
            
            console.log('✅ Password reset successful');

            return { 
                success: true, 
                message: 'Password has been reset successfully' 
            };

        } catch (error) {
            console.error('❌ Password reset verification error:', error);
            
            let message = 'Unable to reset password. Please try again.';
            
            if (error.code === 'auth/expired-action-code') {
                message = 'This password reset link has expired. Please request a new one.';
            } else if (error.code === 'auth/invalid-action-code') {
                message = 'This password reset link is invalid. Please request a new one.';
            } else if (error.code === 'auth/weak-password') {
                message = 'Password is too weak. Please choose a stronger password.';
            }

            return { 
                success: false, 
                message: message,
                code: error.code 
            };
        }
    }

    /**
     * Check if password reset code is valid
     */
    async verifyResetCodeValidity(code) {
        try {
            const email = await auth.verifyPasswordResetCode(code);
            console.log('✅ Password reset code is valid for:', email);
            return { success: true, email: email };
        } catch (error) {
            console.error('❌ Invalid password reset code:', error);
            return { success: false, error: error.code };
        }
    }

    /**
     * Create forgot password dialog HTML
     */
    getForgotPasswordDialogHTML() {
        return `
            <!-- Forgot Password Dialog -->
            <div class="auth-overlay" id="forgotPasswordOverlay">
                <div class="auth-overlay-content">
                    <div class="auth-dialog-header">
                        <button class="auth-close-btn" onclick="window.authDialogs.closeDialog()">&times;</button>
                        <div class="auth-dialog-logo">DeckOps.ai</div>
                        <h2 class="auth-dialog-title">Reset Password</h2>
                        <p class="auth-dialog-subtitle">We'll send you a secure link to reset your password</p>
                    </div>
                    
                    <div class="auth-dialog-content">
                        <form id="forgotPasswordForm" class="auth-form">
                            <div class="auth-form-group">
                                <label class="auth-form-label">Email Address</label>
                                <div class="auth-form-input-container">
                                    <input type="email" class="auth-form-input" id="forgotPasswordEmail" placeholder="Enter your email address" required>
                                    <div class="auth-form-icon">📧</div>
                                </div>
                                <div class="auth-form-error" id="forgotPasswordEmailError"></div>
                            </div>

                            <button type="submit" class="auth-btn-primary" id="sendResetEmailButton">
                                Send Reset Email
                            </button>
                        </form>

                        <div class="auth-forgot-password">
                            <a href="#" class="auth-link" onclick="window.authDialogs.showSignIn()">Back to Sign In</a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Password Reset Success Dialog -->
            <div class="auth-overlay" id="passwordResetSuccessOverlay">
                <div class="auth-overlay-content">
                    <div class="auth-dialog-header">
                        <button class="auth-close-btn" onclick="window.authDialogs.closeDialog()">&times;</button>
                        <div class="auth-dialog-logo">DeckOps.ai</div>
                        <h2 class="auth-dialog-title">Check Your Email</h2>
                        <p class="auth-dialog-subtitle">Password reset instructions have been sent</p>
                    </div>
                    
                    <div class="auth-dialog-content">
                        <div class="auth-verification-notice">
                            <div class="auth-verification-icon">📧</div>
                            <h3>Reset Email Sent</h3>
                            <p>We've sent password reset instructions to <strong id="resetEmailAddress"></strong></p>
                            <p>Please check your email and follow the instructions to reset your password.</p>
                        </div>

                        <div class="auth-step-navigation">
                            <button type="button" class="auth-btn-primary" onclick="window.authDialogs.showSignIn()">Back to Sign In</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

const passwordResetService = new PasswordResetService();
window.passwordResetService = passwordResetService;

export default passwordResetService;
